import type { CatalogProduct, CatalogProductFieldDefinition } from '../../types'

export type ProductCustomFieldRow = {
  field: CatalogProductFieldDefinition
  value: unknown
}

export type ProductCustomFieldSection = {
  id: string
  title: string
  zone: CatalogProductFieldDefinition['storefront']['zone']
  layoutType?: NonNullable<CatalogProductFieldDefinition['layoutSection']>['layoutType']
  order: number
  rows: ProductCustomFieldRow[]
}

export type ProductCustomFieldGroups = {
  summaryCustomRows: ProductCustomFieldRow[]
  highlightsCustomRows: ProductCustomFieldRow[]
  sidebarCustomRows: ProductCustomFieldRow[]
  specsCustomRows: ProductCustomFieldRow[]
  detailCustomRows: ProductCustomFieldRow[]
  specsSections: ProductCustomFieldSection[]
  detailSections: ProductCustomFieldSection[]
}

const DEFAULT_SECTION_TITLE_BY_ZONE: Record<CatalogProductFieldDefinition['storefront']['zone'], string> = {
  summary: 'Summary',
  highlights: 'Highlights',
  specs: 'Dettagli tecnici',
  details: 'Informazioni aggiuntive',
  sidebar: 'Info utili',
}

const DEFAULT_SECTION_ID_BY_ZONE: Record<CatalogProductFieldDefinition['storefront']['zone'], string> = {
  summary: 'summary-main',
  highlights: 'highlights-main',
  specs: 'specs-main',
  details: 'details-overview',
  sidebar: 'sidebar-main',
}

const SECTION_TITLE_BY_ID: Record<string, string> = {
  'summary-main': 'Summary',
  'highlights-main': 'Highlights',
  'specs-main': 'Dettagli tecnici',
  'details-overview': 'Panoramica',
  'details-technical-specs': 'Dettagli tecnici',
  'details-additional': 'Informazioni aggiuntive',
  'sidebar-main': 'Info utili',
}

export const normalizeProductType = (value: string | undefined) =>
  (value ?? '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40)

export const isHttpUrl = (value: string) => {
  try {
    const parsed = new URL(value)
    return parsed.protocol === 'http:' || parsed.protocol === 'https:'
  } catch {
    return false
  }
}

export const resolveProductType = ({
  productData,
  productFieldDefinitions,
}: {
  productData: CatalogProduct | null
  productFieldDefinitions: CatalogProductFieldDefinition[]
}) => {
  const customFields = productData?.customFields ?? {}
  const explicit = normalizeProductType(productData?.productType)
  if (explicit) return explicit

  const customFieldKeys = new Set(Object.keys(customFields))
  if (!customFieldKeys.size) return ''

  const hitCountByType = new Map<string, number>()
  productFieldDefinitions.forEach((field) => {
    if (!customFieldKeys.has(field.key)) return
    hitCountByType.set(field.productType, (hitCountByType.get(field.productType) ?? 0) + 1)
  })

  let winner = ''
  let winnerHits = 0
  hitCountByType.forEach((hits, productType) => {
    if (hits > winnerHits) {
      winner = productType
      winnerHits = hits
    }
  })
  return winner
}

export const buildProductCustomRows = ({
  customFields,
  productFieldDefinitions,
  resolvedProductType,
}: {
  customFields: Record<string, unknown>
  productFieldDefinitions: CatalogProductFieldDefinition[]
  resolvedProductType: string
}) => {
  const activeProductFields = productFieldDefinitions
    .filter((field) => (resolvedProductType ? field.productType === resolvedProductType : true))
    .sort((left, right) => {
      const orderDiff = left.storefront.order - right.storefront.order
      if (orderDiff !== 0) return orderDiff
      return left.label.localeCompare(right.label, 'it', { sensitivity: 'base' })
    })

  const customFieldRows = activeProductFields
    .map((field) => ({ field, value: customFields[field.key] }))
    .filter(({ value }) => {
      if (value === null || value === undefined) return false
      if (typeof value === 'string') return value.trim().length > 0
      if (Array.isArray(value)) return value.length > 0
      return true
    })

  const groups = customFieldRows.reduce<ProductCustomFieldGroups>(
    (groups, row) => {
      const zone = row.field.storefront.zone
      if (zone === 'summary') groups.summaryCustomRows.push(row)
      else if (zone === 'highlights') groups.highlightsCustomRows.push(row)
      else if (zone === 'sidebar') groups.sidebarCustomRows.push(row)
      else if (zone === 'specs') groups.specsCustomRows.push(row)
      else groups.detailCustomRows.push(row)
      return groups
    },
    {
      summaryCustomRows: [],
      highlightsCustomRows: [],
      sidebarCustomRows: [],
      specsCustomRows: [],
      detailCustomRows: [],
      specsSections: [],
      detailSections: [],
    },
  )

  return {
    ...groups,
    specsSections: buildSectionsForZone(groups.specsCustomRows, 'specs'),
    detailSections: buildSectionsForZone(groups.detailCustomRows, 'details'),
  }
}

const buildSectionsForZone = (
  rows: ProductCustomFieldRow[],
  zone: CatalogProductFieldDefinition['storefront']['zone'],
): ProductCustomFieldSection[] => {
  const sections = new Map<string, ProductCustomFieldSection>()

  rows.forEach((row) => {
    const layoutSection = row.field.layoutSection
    const sectionId = row.field.storefront.sectionId || layoutSection?.id || DEFAULT_SECTION_ID_BY_ZONE[zone]
    const existing = sections.get(sectionId)
    if (existing) {
      existing.rows.push(row)
      return
    }

    sections.set(sectionId, {
      id: sectionId,
      title: layoutSection?.title || SECTION_TITLE_BY_ID[sectionId] || DEFAULT_SECTION_TITLE_BY_ZONE[zone],
      zone,
      layoutType: layoutSection?.layoutType,
      order: layoutSection?.order ?? row.field.storefront.order,
      rows: [row],
    })
  })

  return Array.from(sections.values()).sort((left, right) => {
    if (left.order !== right.order) return left.order - right.order
    return left.title.localeCompare(right.title, 'it', { sensitivity: 'base' })
  })
}

export const getZoomGallery = (activeProductImage: string, productGallery: string[]) =>
  Array.from(
    new Set(
      [...productGallery, activeProductImage]
        .map((item) => item.trim())
        .filter(Boolean),
    ),
  )
